/* eslint-disable @typescript-eslint/ban-ts-ignore */
import React from 'react';
import styled from 'styled-components/macro';
import { useFormatMessage } from 'components/IntlProvider';
import { Input, Button, Typography } from '@deeptrue-frontend/ui';
import { FormatMessage } from 'components/IntlProvider/hooks/hooks';
import { Formik, Field as FormikField } from 'formik';
import * as Yup from 'yup';
import EditorText from './Editor';
import {
  generatePreview,
  ctaBlock,
} from 'components/Project/EmailTemplates/generatePreview';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  align-items: center;

  & label {
    max-width: 40%;
    margin-bottom: 0;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
`;

const InputTemplate = styled(Input)``;

const SmallButton = styled(Button)`
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
`;

const CancelButton = styled(SmallButton)`
  border: 0;
`;

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background-color: #e3e5e8;
  margin-bottom: 1rem;
`;

const ContentWrapper = styled.div`
  padding: 0 1rem;
`;

const EditorWrapper = styled.div`
  border: 1px solid #e3e5e8;
  box-sizing: border-box;
  box-shadow: 0px 2px 4px rgba(63, 63, 68, 0.041958),
    0px 1px 1px rgba(63, 63, 68, 0.0769231);
  border-radius: 4px;
 
  margin: 0 0 2rem 0;

  .rdw-editor-main {
    min-height: 400px;
    padding: 0 1rem;
  }
}
`;

export interface FormValues {
  templateName: string;
  subject: string;
  text_content: any;
}

interface Props {
  onSubmit: (val: any) => void;
  initialValues: FormValues;
  onCancel: () => void;
  isNewTemplate: boolean;
}

const getValidationSchema = (t: FormatMessage) =>
  Yup.object().shape<FormValues>({
    templateName: Yup.string().required(
      t('errors.fields.required', 'Field is required')
    ),
    subject: Yup.string().required(
      t('errors.fields.required', 'Field is required')
    ),
    text_content: Yup.string()
      .required(t('errors.fields.required', 'Field is required'))
      .test('len', 'err', (val) => {
        const isSaveButtonAdded = val.match(ctaBlock);

        return isSaveButtonAdded;
      }),
  });

const TemplateCreator = ({
  initialValues,
  onSubmit,
  onCancel,
  isNewTemplate,
}: Props) => {
  const t = useFormatMessage();

  return (
    <Wrapper>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={getValidationSchema(t)}
      >
        {({
          handleSubmit,
          errors,
          touched,
          isSubmitting,
          setFieldValue,
          values,
        }) => {
          return (
            <>
              <Header>
                <FormikField
                  name="templateName"
                  hasError={touched.templateName && errors.templateName}
                  helperText={touched.templateName && errors.templateName}
                  as={InputTemplate}
                  data-testid="templateName"
                />
                <ButtonWrapper>
                  <CancelButton onClick={onCancel} variant="outlined">
                    {t('templates.base.cancel', 'Cancel')}
                  </CancelButton>
                  <SmallButton
                    ml="1rem"
                    variant="outlined"
                    onClick={() => {
                      generatePreview(values.text_content, false);
                    }}
                  >
                    {t('templates.base.preview', 'Preview')}
                  </SmallButton>
                  <SmallButton
                    ml="1rem"
                    isLoading={isSubmitting}
                    variant="primary"
                    // @ts-ignore
                    onClick={handleSubmit}
                  >
                    {isNewTemplate
                      ? t('templates.base.saveAs', 'Save as New Template')
                      : t('templates.base.update', 'Update template')}
                  </SmallButton>
                </ButtonWrapper>
              </Header>
              <Separator />
              <ContentWrapper>
                <Typography
                  color="#13244C"
                  fontSize="0.875rem"
                  variant="copy1"
                  weight="medium"
                  mb="0.5rem"
                >
                  {t('templates.base.subject', 'Subject')}
                </Typography>

                <FormikField
                  name="subject"
                  hasError={touched.subject && errors.subject}
                  helperText={touched.subject && errors.subject}
                  as={Input}
                  data-testid="subject"
                />
                <Typography
                  color="#13244C"
                  fontSize="0.875rem"
                  variant="copy1"
                  weight="medium"
                  mb="0.5rem"
                  mt="1rem"
                >
                  {t('templates.base.text', 'Text')}
                </Typography>
                {touched.text_content && errors.text_content && (
                  <Typography color="red">
                    {t(
                      'templates.base.missingCTA',
                      'Please add a CTA button that links to your survey.'
                    )}
                  </Typography>
                )}
                <EditorWrapper>
                  <EditorText
                    textContent={values.text_content}
                    setFieldValue={setFieldValue}
                  />
                </EditorWrapper>
              </ContentWrapper>
            </>
          );
        }}
      </Formik>
    </Wrapper>
  );
};

export default TemplateCreator;
